import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  NbComponentStatus,
  NbDialogService,
  NbGlobalPhysicalPosition,
  NbIconLibraries,
  NbToastrService,
} from "@nebular/theme";
import { replace, remove, clone, find } from "lodash";

import { User } from "../../@core/data/users";
import { UserService } from "../../@core/services/user.service";
import { convertTimestamp } from "../../@core/utils/convertTimestamps.service";

import ReportSpam from "../../models/report-spam.model";
import Course from "../../models/course.model";
import LectureContent from "../../models/lecture-content.model";
import QnA, { QnAReply } from "../../models/qna.model";
import Quiz from "../../models/quiz.model";
import Comment from "../../models/comment.model";

import { DialogReportSpamComponent } from "../dialog-report-spam/dialog-report-spam.component";
import { ReportSpamService } from "../../services/report-spam.service";
import CourseRating from "../../models/course-rating.model";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import Exercise from "../../models/exercise.model";
import Announcement from "../../models/announcement.model";
import Enrollment from "../../models/enrollment.model";

@Component({
  selector: "ngx-button-report-spam",
  styleUrls: ["./button-report-spam.component.scss"],
  templateUrl: "./button-report-spam.component.html",
})
export class ButtonReportSpamComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();
  @Input() course? = new Course();
  @Input() rating? = new CourseRating();
  @Input() lecture? = new LectureContent();
  @Input() exercise? = new Exercise();
  @Input() quiz? = new Quiz();
  @Input() comment? = new Comment();
  @Input() qna? = new QnA();
  @Input() qnaReply? = new QnAReply();
  @Input() announcement? = new Announcement();
  @Input() student? = new Enrollment();

  @Output() onReport: EventEmitter<any> = new EventEmitter();

  submitted = false;
  loading = false;

  reported?: ReportSpam = new ReportSpam();
  uid = "anonymous";
  isLogin = false;

  constructor(
    public userService: UserService,
    private reportService: ReportSpamService,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService
  ) {}

  ngOnInit() {
    this.init();
    this.userService
      .getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user: User) => {
        if (user && user.uid != this.uid) {
          this.uid = user.uid;
          this.isLogin = true;
        } else {
          this.uid = "anonymous";
          this.isLogin = false;
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  report() {
    if (this.isLogin) {
      this.dialogService
        .open(DialogReportSpamComponent, {
          context: {},
          closeOnBackdropClick: false,
        })
        .onClose.subscribe((item) => {
          if (item) {
            this.reported.value = item.value;
            this.reported.reason = item.reason;
            this.create();
          }
        });
    }
    // else {
    //   this.dialogService
    //     .open(DialogAuthLoginComponent, {
    //       context: {
    //         mode: "header",
    //       },
    //       closeOnBackdropClick: false,
    //     })
    //     .onClose.subscribe((user) => {
    //       if (user) {
    //         console.log("DialogAuthLoginComponent/login:", user);
    //         this.uid = user.uid;
    //         this.isLogin = true;
    //       }
    //     });
    // }
  }

  create() {
    this.loading = true;
    this.submitted = true;

    this.reportService
      .create(this.reported)
      .then((value) => {
        this.reported = clone(value);
        this.loading = false;
        this.submitted = false;
        this.showToast("success", "Báo vi phạm", "thành công");

        this.onReport.emit(this.reported);
      })
      .catch((err) => {
        this.loading = false;
        this.submitted = false;
        this.showToast("warning", "Báo vi phạm", "thất bại");
      });
  }

  private init() {
    this.reported = new ReportSpam();
    this.reported.id = "";
    this.reported.value = -1;
    this.reported.reason = "";
    if (this.course && this.course.id) {
      this.reported.mode = "courses";
      this.reported.refId = this.course.id;
      this.reported.refTitle = this.course.title;
      this.reported.refUid = this.course.createdBy;
      this.reported.refAuthor = this.course.author;
      this.reported.refApproved = this.course.isApproved;
      this.reported.refUrl = `/course/${this.course.id}`;
      this.reported.courseId = this.course.id;
    } else if (this.lecture && this.lecture.id) {
      this.reported.mode = "lecture-contents";
      this.reported.refId = this.lecture.id;
      this.reported.refTitle = this.lecture.title;
      this.reported.refUid = this.lecture.createdBy;
      this.reported.refAuthor = this.lecture.author;
      this.reported.refApproved = this.lecture.isApproved;
      this.reported.refUrl = `/lecture/${this.lecture.id}`;
      this.reported.courseId = this.lecture.courseId
        ? this.lecture.courseId
        : "";
    } else if (this.exercise && this.exercise.id) {
      this.reported.mode = "exercises";
      this.reported.refId = this.exercise.id;
      this.reported.refTitle = this.exercise.title;
      this.reported.refUid = this.exercise.createdBy;
      this.reported.refAuthor = this.exercise.author;
      this.reported.refApproved = this.exercise.isApproved;
      this.reported.refUrl = `/exercise/${this.exercise.id}`;
      this.reported.courseId = this.exercise.courseId
        ? this.exercise.courseId
        : "";
    } else if (this.qna && this.qna.id) {
      this.reported.mode = "qnas";
      this.reported.refId = this.qna.id;
      this.reported.refTitle = this.qna.title;
      this.reported.refUid = this.qna.createdBy;
      this.reported.refAuthor = this.qna.author;
      this.reported.refApproved = this.qna.isApproved;
      this.reported.refUrl = `/qna/${this.qna.id}`;
      this.reported.courseId = this.qna.courseId ? this.qna.courseId : "";
    } else if (this.qnaReply && this.qnaReply.qnaId && this.qnaReply.id) {
      this.reported.mode = `qnas/${this.qnaReply.qnaId}/replies`;
      this.reported.refId = this.qnaReply.id;
      this.reported.refTitle = this.qnaReply.content;
      this.reported.refUid = this.qnaReply.createdBy;
      this.reported.refAuthor = this.qnaReply.author;
      this.reported.refApproved = this.qnaReply.isApproved;

      this.reported.refDescription = this.qnaReply.qnaTitle;
      this.reported.refUrl = `/qna/${this.qnaReply.qnaId}`;
      this.reported.courseId = "";
    } else if (this.quiz && this.quiz.id) {
      this.reported.mode = "quizzes";
      this.reported.refId = this.quiz.id;
      this.reported.refTitle = this.quiz.title;
      this.reported.refUid = this.quiz.createdBy;
      this.reported.refAuthor = this.quiz.author;
      this.reported.refApproved = this.quiz.isApproved;
      this.reported.refUrl = `/quiz/${this.quiz.id}`;
      this.reported.courseId = "";
    } else if (this.comment && this.comment.id) {
      this.reported.mode = "comments";
      this.reported.refId = this.comment.id;
      this.reported.refTitle = this.comment.content;
      this.reported.refUid = this.comment.createdBy;
      this.reported.refAuthor = this.comment.author;
      this.reported.refApproved = this.comment.isApproved;
      this.reported.refDescription = this.comment.refTitle;
      this.reported.refUrl = this.comment.refUrl;
      this.reported.courseId = "";
    } else if (this.rating && this.rating.id) {
      this.reported.mode = `courses/${this.rating.courseId}/ratings`;
      this.reported.refId = this.rating.id;
      this.reported.refTitle = this.rating.title;
      this.reported.refUid = this.rating.createdBy;
      this.reported.refAuthor = this.rating.author;
      this.reported.refApproved = this.rating.isApproved;
      this.reported.refUrl = `/course/${this.rating.courseId}`;
      this.reported.courseId = this.rating.courseId ? this.rating.courseId : "";
    } else if (this.announcement && this.announcement.id) {
      this.reported.mode = "announcements";
      this.reported.refId = this.announcement.id;
      this.reported.refTitle = this.announcement.title;
      this.reported.refUid = this.announcement.createdBy;
      this.reported.refAuthor = this.announcement.author;
      this.reported.refApproved = this.announcement.isApproved;
      this.reported.refUrl = `/announcement/${this.announcement.id}`;
      this.reported.courseId = this.announcement.courseId
        ? this.announcement.courseId
        : "";
    } else if (this.student && this.student.id) {
      this.reported.mode = "enrollments";
      this.reported.refId = this.student.id;
      this.reported.refTitle = this.student.author.email;
      this.reported.refUid = this.student.createdBy;
      this.reported.refAuthor = this.student.author;
      this.reported.refApproved = true;
      this.reported.refUrl = `/enrollments/${this.student.id}`;
      this.reported.courseId = this.student.courseId
        ? this.student.courseId
        : "";
    }

    this.reported.refApproved = this.reported.refApproved
      ? this.reported.refApproved
      : false;
  }

  // control event
  private showToast(type: NbComponentStatus, title: string, body: string) {
    const config = {
      status: type,
      destroyByClick: true,
      duration: 2000,
      hasIcon: true,
      position: NbGlobalPhysicalPosition.TOP_RIGHT,
      preventDuplicates: false,
    };
    const titleContent = title ? `${title}` : "";

    this.toastrService.show(body, titleContent, config);
  }
}
